import { environmentHttpClient as httpClient } from "client/Api/httpClient";
import { OnlineMeeting } from "client/Api/Notifications/notifications";
import { AppointmentRoomModel } from "../Email/Rooms/room";
import { WorkingDaysModel } from "../UserProfile/service";
import { ExchangeMessageBodyKind } from "../Email/common";
import { DisplayContactModel } from "../Email/contacts";

export enum CalendarContentViewKind {
	Day = 0,
	Week = 1,
	ThreeDays = 2,
	FiveDays = 3,
	WorkWeek = 4
}

export type CalendarPageViewModel = {
	calendars: readonly CalendarInfoModel[];
	currentUserWorkingDays: WorkingDaysModel;
	liveUpdateInterval: number;
	defaultView: CalendarContentViewKind;
};

export function getViewModel(signal: AbortSignal): Promise<CalendarPageViewModel> {
	return httpClient.get<CalendarPageViewModel>(
		"UiApi/Calendar",
		undefined,
		{ signal }
	);
}

export function registerDefaultView(
	view: CalendarContentViewKind,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"UiApi/Calendar/RegisterDefaultView",
		view,
		{ signal }
	);
}

export function getAppointments(
	filter: CalendarFilterModel,
	signal?: AbortSignal
): Promise<CalendarAppointmentsResult> {
	return httpClient.post<CalendarAppointmentsResult>(
		"UiApi/Calendar/GetAppointments",
		filter,
		{ signal }
	);
}

export type AppointmentIdentifier = {
	id: string;
	// When calendarId is not sent to backend
	// it will be considered as personal calendar
	calendarId?: string;
};

export function getAppointmentDetails(
	identifier: AppointmentIdentifier,
	signal?: AbortSignal
): Promise<AppointmentDetailViewModel> {
	return httpClient.get<AppointmentDetailViewModel>(
		"UiApi/Calendar/GetAppointmentDetails",
		identifier,
		{ signal }
	);
}

export function acceptAppointment(
	identifier: AppointmentIdentifier,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"UiApi/Calendar/Accept",
		identifier,
		{ signal }
	);
}

export function declineAppointment(
	identifier: AppointmentIdentifier,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"UiApi/Calendar/Decline",
		identifier,
		{ signal }
	);
}

export type OrganizerModel = {
	emailAddress: string;
	displayName: string;
};

export enum AttendingStatus {
	Unknown = 0,
	Accept = 1,
	Decline = 2,
	Tenative = 3
}

export enum AttendeeKind {
	Unknown = 0,
	Required = 1,
	Optional = 2,
	Resource = 3
}

export type AttendeeModel = {
	emailAddress: string;
	displayName: string;
	status: AttendingStatus;
	kind: AttendeeKind;
};

export type CalendarRelationInfo = {
	name: string;
	canEdit: boolean;
	id: string;
};

export type LocationModel = {
	id: string | null;
	displayName: string;
};

export type AppointmentDetailModel = {
	id: string;
	masterId: string | null;
	subject: string | null;
	start: Date | string;
	end: Date | string;
	isAllDayEvent: boolean;
	locations: readonly LocationModel[];
	description: string | null;
	descriptionKind: ExchangeMessageBodyKind;
	meeting: OnlineMeeting | null;
	reminderBeforeInMinutes: number | null;
	organizer: OrganizerModel | null;
	attendees: readonly AttendeeModel[];
	myResponseStatus: AppointmentResponseStatus | null;
	isCancelled: boolean;
	calendar: CalendarRelationInfo;
	isOrganizer: boolean;
	isPrivate: boolean;
	recurrenceOptions: RecurrenceOptionsModel | null;
};

export type AppointmentDetailViewModel = {
	shouldAutomaticallyShowSenderImages: boolean;
	model: AppointmentDetailModel;
};

export enum AppointmentResponseKind {
	Organizer = 1,
	Tentative = 2,
	Accept = 3,
	Decline = 4,
	NoResponseReceived = 5
}

export type AppointmentResponseStatus = {
	isResponseOutstanding: boolean;
	responseKind: AppointmentResponseKind;
};

export enum CalendarKind {
	Personal = 1,
	Shared = 2,
	Contact = 3
}

export type WorkspaceCalendarConfiguration = {
	id: number;
	color: CalendarColor;
};

export type CalendarInfoModel = {
	id: string;
	name: string;
	kind: CalendarKind;
	isDefault: boolean;
	canEdit: boolean;
	configuration: WorkspaceCalendarConfiguration | null;
};

export type CalendarFilterModel = {
	id: string;
	start: string;
	end: string;
};

export type CalendarAppointmentsResult = {
	status: CalendarAppointmentsLoadStatus;
	id: string;
	appointments: readonly AppointmentViewModel[];
};

export type AppointmentViewModel = {
	id: string | null;
	masterId: string | null;
	subject: string | null;
	start: string;
	end: string;
	isAllDayEvent: boolean;
	locations: readonly string[] | null;
	meeting: OnlineMeeting | null;
	isRecurring: boolean;
	hasAttachments: boolean;
	isCancelled: boolean;
	isPrivate: boolean;
	myResponseStatus: AppointmentResponseStatus | null;
	isOrganizer: boolean;
};

export enum CalendarAppointmentsLoadStatus {
	NoError = 0,
	UnknownError = 1,
	AccessDenied = 2
};

export enum CalendarColor {
	Info = 0,
	Success = 1,
	Warning = 2,
	Error = 3,
	Color1 = 4,
	Color2 = 5,
	Color3 = 6,
	Color4 = 7,
	Color5 = 8,
	Color6 = 9,
	Color7 = 10,
	Color8 = 11,
	Color9 = 12,
	Color10 = 13,
	Color11 = 14,
	Color12 = 15,
	Color13 = 16,
	Color14 = 17,
	Color15 = 18,
	Color16 = 19
}

export type CalendarSaveRequestModel = {
	id: string;
	color: CalendarColor;
};

export type CalendarSaveResultModel = {
	id: number;
};

export function enableCalendar(
	calendar: CalendarSaveRequestModel,
	signal?: AbortSignal
): Promise<CalendarSaveResultModel> {
	return httpClient.post<CalendarSaveResultModel>(
		"UiApi/Calendar/Enabled",
		calendar,
		{ signal }
	);
}

export function disableCalendar(
	id: number,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.delete<void>(
		`UiApi/Calendar/Enabled/${id}`,
		undefined,
		{ signal }
	);
}

export type AttendeeSaveModel = {
	emailAddress: string;
};

export type AppointmentLocationSaveModel = {
	displayName: string;
} | AppointmentRoomModel;

export enum AppointmentRecurrencePatternKind {
	Daily = 1,
	Weekly = 2,
	AbsoluteMonthly = 3,
	RelativeMonthly = 4,
	AbsoluteYearly = 5,
	RelativeYearly = 6
}

export enum AppointmentDayOfWeek {
	Sunday = 0,
	Monday = 1,
	Tuesday = 2,
	Wednesday = 3,
	Thursday = 4,
	Friday = 5,
	Saturday = 6
}

export enum AppointmentWeekIndex {
	First = 0,
	Second = 1,
	Third = 2,
	Fourth = 3,
	Last = 4,
}

export enum AppointmentRecurrenceRangeKind {
	EndDate = 1,
	NoEnd = 2,
	Numbered = 3
}

export type BaseRecurrencePatternModel = {
	repeatInterval: number;
};

export type DailyRecurrencePatternModel = {
	kind: AppointmentRecurrencePatternKind.Daily;
};

export type WeeklyRecurrencePatternModel = {
	kind: AppointmentRecurrencePatternKind.Weekly;
	daysOfWeek: readonly AppointmentDayOfWeek[];
};

export type AbsoluteMonthlyRecurrencePatternModel = {
	kind: AppointmentRecurrencePatternKind.AbsoluteMonthly;
	dayOfMonth: number;
};

export type RelativeMonthlyRecurrencePatternModel = {
	kind: AppointmentRecurrencePatternKind.RelativeMonthly;
	daysOfWeek: readonly AppointmentDayOfWeek[];
	weekIndex: AppointmentWeekIndex;
};

export type AbsoluteYearlyRecurrencePatternModel = {
	kind: AppointmentRecurrencePatternKind.AbsoluteYearly;
	dayOfMonth: number;
	monthIndex: number;
};

export type RelativeYearlyRecurrencePatternModel = {
	kind: AppointmentRecurrencePatternKind.RelativeYearly;
	monthIndex: number;
	weekIndex: AppointmentWeekIndex;
	daysOfWeek: readonly AppointmentDayOfWeek[];
};

export type RecurrencePatternModel = BaseRecurrencePatternModel & (
	| DailyRecurrencePatternModel
	| WeeklyRecurrencePatternModel
	| AbsoluteMonthlyRecurrencePatternModel
	| RelativeMonthlyRecurrencePatternModel
	| AbsoluteYearlyRecurrencePatternModel
	| RelativeYearlyRecurrencePatternModel
);

export type EndDateRecurrenceRangeModel = {
	kind: AppointmentRecurrenceRangeKind.EndDate;
	startDate: Date | string;
	endDate: Date | string;
};

export type NoEndDateRecurrenceRangeModel = {
	kind: AppointmentRecurrenceRangeKind.NoEnd;
	startDate: Date | string;
};

export type NumberedRecurrenceRangeModel = {
	kind: AppointmentRecurrenceRangeKind.Numbered;
};

export type RecurrenceRangeModel = EndDateRecurrenceRangeModel
	| NoEndDateRecurrenceRangeModel
	| NumberedRecurrenceRangeModel;

export type RecurrenceOptionsModel = {
	pattern: RecurrencePatternModel;
	range: RecurrenceRangeModel;
};

export type CreateCalendarAppointmentModel = {
	calendarId: string;
	isPrivate: boolean;
	title: string;
	startTime: string;
	endTime: string;
	isAllDay: boolean;
	reminderMinutes: number | null;
	attendees: readonly AttendeeSaveModel[];
	locations: readonly AppointmentLocationSaveModel[];
	description: string;
	recurrenceOptions: RecurrenceOptionsModel | null;
	isTeamsMeeting: boolean;
};

export type CalendarAppointmentSaveResult = {
	id: string;
};

export function addAppointment(
	saveModel: CreateCalendarAppointmentModel,
	signal?: AbortSignal
): Promise<CalendarAppointmentSaveResult> {
	return httpClient.post<CalendarAppointmentSaveResult>(
		"UiApi/Calendar/Appointments",
		saveModel,
		{ signal }
	);
}

export enum RecurringAppointmentOperationKind {
	Current = 1,
	CurrentAndFollowing = 2,
	All = 3
}

export type CancelCalendarAppointmentModel = {
	identifier: AppointmentIdentifier;
	comment: string;
	recurringOperationKind: OptionalModel<RecurringAppointmentOperationKind>;
};

export function cancelAppointment(
	model: CancelCalendarAppointmentModel,
	signal?: AbortSignal
): Promise<CalendarAppointmentSaveResult> {
	return httpClient.put<CalendarAppointmentSaveResult>(
		"UiApi/Calendar/Appointments/cancel",
		model,
		{ signal }
	);
}

export type DeleteCalendarAppointmentModel = {
	identifier: AppointmentIdentifier;
	recurringOperationKind: OptionalModel<RecurringAppointmentOperationKind>;
};

export function deleteAppointment(
	model: DeleteCalendarAppointmentModel,
	signal?: AbortSignal
): Promise<CalendarAppointmentSaveResult> {
	return httpClient.delete<CalendarAppointmentSaveResult>(
		"UiApi/Calendar/Appointments",
		model,
		{ signal }
	);
}

export type RecurrenceTargetModel = {
	recurringOperationKind: RecurringAppointmentOperationKind;
	recurrenceOptions: OptionalModel<RecurrenceOptionsModel>;
};

export type UpdateCalendarAppointmentModel = {
	calendarId: string;
	title: string;
	startTime: string;
	endTime: string;
	isAllDay: boolean;
	attendees: readonly AttendeeSaveModel[];
	locations: readonly AppointmentLocationSaveModel[];
	description: string;
	isPrivate: OptionalModel<boolean>;
	reminderMinutes: OptionalModel<number | null>;
	recurrenceTarget: RecurrenceTargetModel | null;
	isTeamsMeeting: OptionalModel<boolean>;
};

export function updateCalendarAppointment(
	id: string,
	model: UpdateCalendarAppointmentModel,
	signal?: AbortSignal
): Promise<CalendarAppointmentSaveResult> {
	return httpClient.put<CalendarAppointmentSaveResult>(
		`UiApi/Calendar/Appointments/${id}`,
		model,
		{ signal }
	);
}

export enum AddContactCalendarStatus {
	NoError = 0,
	UnknownError = 1,
	AccessDenied = 2,
	AlreadyExists = 3
}

export type AddContactCalendarResultModel = {
	status: AddContactCalendarStatus.NoError;
	calendarInfo: CalendarInfoModel;
} | {
	status: Exclude<AddContactCalendarStatus, AddContactCalendarStatus.NoError>;
};

export type ContactCalendarRequestModel = {
	contactId: string;
	color: CalendarColor;
};

export function addContactCalendar(
	model: ContactCalendarRequestModel,
	signal?: AbortSignal
): Promise<AddContactCalendarResultModel> {
	return httpClient.post<AddContactCalendarResultModel>(
		"UiApi/Calendar/ContactCalendar",
		model,
		{ signal }
	);
}

export function deleteContactCalendar(
	calendarId: string,
	signal?: AbortSignal
): Promise<AddContactCalendarResultModel> {
	return httpClient.delete<AddContactCalendarResultModel>(
		"UiApi/Calendar/ContactCalendar",
		{ calendarId },
		{ signal }
	);
}

export function findContactCalendars(
	searchInput: string,
	signal?: AbortSignal
): Promise<readonly DisplayContactModel[]> {
	return httpClient.get<readonly DisplayContactModel[]>(
		"UiApi/Calendar/Contacts",
		{ searchInput },
		{ signal }
	);
}

export type SchedulesAvailabilityRequestModel = {
	startTime: string;
	endTime: string;
	smtpAddresses: readonly string[];
};

export enum ScheduleAvailabilityKind {
	Unknown = 0,
	Available = 1,
	Unavailable = 2
}

export function getScheduleAvailabilities(
	model: SchedulesAvailabilityRequestModel,
	signal: AbortSignal
): Promise<Record<string, ScheduleAvailabilityKind>> {
	return httpClient.get<Record<string, ScheduleAvailabilityKind>>(
		"UiApi/Calendar/Availabilities",
		model,
		{ signal }
	);
}